import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Car, Home, PiggyBank, Landmark, Gift, Calendar, Flag } from "lucide-react";
import Reveal from "../Plan2Benefit/Reveal.jsx";
import SectionHeading from "../Plan2Benefit/SectionHeading.jsx";
import WithStar from "./WithStar.jsx";
import { journeySteps } from "./data.js";

const STEP_ICONS = {
  "choose-car": Car,
  "test-drive": Home,
  "down-payment": PiggyBank,
  loan: Landmark,
  benefits: Gift,
  "car-day": Calendar,
  "drive-home": Flag,
};

/* ─────────────────────────────────────────────────────────────
   JourneyTimeline — the seven-step Car Plan journey as an icon-led
   vertical timeline with card content. Chosen as the final
   treatment after an A/B/C comparison against HowItWorks.jsx and
   JourneyStory.jsx, both since deleted.

   Bookended on purpose: the first step ("start here") and the
   last, arrival step both get a filled icon circle with a soft
   drop shadow, while every step in between stays a lighter outline
   — a start/middle/end rhythm rather than seven identical rows.
   The connecting line itself runs indigo-to-teal top to bottom,
   echoing the same start → arrival colors — and now fills in as
   you scroll past each step (rather than sitting at one static
   opacity the whole time), so the line reads as live progress
   through the journey, not just a decorative rule between rows.

   Progress is read via a plain (non-pinned) GSAP ScrollTrigger
   rather than framer-motion's own `useScroll` — same reasoning as
   Hero.jsx: this page runs GSAP ScrollSmoother on desktop, which
   virtualizes the scroll position by transforming a content
   wrapper instead of moving the real scrollbar, and framer's
   `useScroll` (which reads native scroll/resize events directly)
   fights it badly enough to crash the tab under Smoother. GSAP's
   own ScrollTrigger already knows how to read Smoother's virtual
   position correctly, so `scrub`-only (no `pin`) gives the same
   0–1 progress without the conflict.
───────────────────────────────────────────────────────────── */
export default function JourneyTimeline() {
  const trackRef = useRef(null);
  const rawRailProgress = useMotionValue(0);
  const railProgress = useSpring(rawRailProgress, { stiffness: 90, damping: 22, mass: 0.3 });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const track = trackRef.current;
    if (!track) return undefined;

    const trigger = ScrollTrigger.create({
      trigger: track,
      start: "top 80%",
      end: "bottom 50%",
      scrub: true,
      onUpdate: (self) => rawRailProgress.set(self.progress),
    });

    return () => trigger.kill();
  }, [rawRailProgress]);

  return (
    <section className="bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <SectionHeading
            eyebrow="Better Experience"
            title="Everything you need."
            highlight="One Car Plan."
          />
        </Reveal>

        <div className="relative mt-14" ref={trackRef}>
          <div
            aria-hidden="true"
            className="absolute left-[27px] top-2 bottom-2 w-[2.5px] overflow-hidden rounded-full bg-[#eceafb] sm:left-[31px]"
          >
            <motion.div
              className="h-full w-full rounded-full"
              style={{
                scaleY: railProgress,
                transformOrigin: "top",
                background: "linear-gradient(to bottom, #4328E7, #24BF9B)",
              }}
            />
          </div>

          <div className="flex flex-col">
            {journeySteps.map((step, i) => {
              const Icon = STEP_ICONS[step.id];
              const isFirst = i === 0;
              return (
                <Reveal key={step.id} delay={0.03 * i}>
                  <div className="relative mb-4 flex items-start gap-6">
                    <span
                      className={`relative z-10 flex h-[54px] w-[54px] flex-none items-center justify-center rounded-full sm:h-[62px] sm:w-[62px] ${
                        step.arrive
                          ? "bg-gradient-to-br from-[#24BF9B] to-[#1a8f74] text-white shadow-[0_10px_22px_-10px_rgba(36,191,155,0.55)]"
                          : isFirst
                          ? "bg-gradient-to-br from-[#4328E7] to-[#6d63ff] text-white shadow-[0_10px_22px_-10px_rgba(67,40,231,0.5)]"
                          : "border-2 border-[#4328E7]/20 bg-white text-[#4328E7]"
                      }`}
                    >
                      {Icon && <Icon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />}
                    </span>

                    <div
                      className={`flex-1 rounded-[16px] border px-5 py-4 ${
                        step.arrive
                          ? "border-[#24BF9B]/25 bg-[#24BF9B]/[0.04]"
                          : isFirst
                          ? "border-[#4328E7]/20 bg-[#4328E7]/[0.04]"
                          : "border-[#eceafb] bg-[#faf9ff]"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-montserrat text-[16px] font-bold tracking-[-0.01em] text-[#0d0828]">
                          {step.title}
                        </h3>
                        <span
                          className={`whitespace-nowrap rounded-full border bg-white px-2.5 py-0.5 font-montserrat text-[10px] font-semibold uppercase tracking-[0.08em] ${
                            step.arrive
                              ? "border-[#24BF9B]/35 text-[#0f9d6e]"
                              : isFirst
                              ? "border-[#4328E7]/25 text-[#4328E7]"
                              : "border-[#eceafb] text-[#0d0828]/45"
                          }`}
                        >
                          {step.tag}
                        </span>
                      </div>
                      <p className="mt-1.5 font-montserrat text-[13.5px] leading-6 text-[#0d0828]/55">
                        <WithStar text={step.description} />
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
